import { Box, Typography } from "@mui/material";
import {
	DndContext,
	DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";

import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { useFieldArray, UseFormReturn, useWatch } from "react-hook-form";
import { CreateSurveyFormFields } from "../survey-form.schema";
import { memo, useCallback } from "react";
import ButtonAddQuestion from "../button-add-question";
import { SurveyFieldType } from "@onair/repositories";
import QuestionItem from "./question-item";

interface SurveyQuestionBoxesProps {
	methods: UseFormReturn<CreateSurveyFormFields>;
}

const initFieldType: CreateSurveyFormFields["fields"][number] = {
	options: [],
	type: SurveyFieldType.TEXTAREA,
	label: "",
	placeholder: "",
	required: false,
	id: "field-1",
};

const SurveyQuestionBoxes: React.FC<SurveyQuestionBoxesProps> = ({
	methods,
}) => {
	const {
		control,
		getValues,
		trigger,
		formState: { errors },
	} = methods;
	const { fields, remove, move, update, append } = useFieldArray({
		control,
		name: "fields",
		keyName: "uuid",
	});
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5,
			},
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		const activeId = active.id;
		const overId = over?.id;

		if (!over || activeId === overId) return;

		const activeIndex = fields.findIndex((field) => field.uuid === activeId);
		const overIndex = fields.findIndex((field) => field.uuid === overId);

		move(activeIndex, overIndex);
	};

	const handleAddQuestion = useCallback(async () => {
		const fieldLength = getValues("fields").length;

		if (fieldLength > 0) {
			const isValidAllFields = await trigger("fields");
			if (!isValidAllFields) return;
		}

		const nextId = `field-${fieldLength + 1}`;
		append({ ...initFieldType, id: nextId });
	}, []);

	const handleChangeRequired = useCallback(
		(checked: boolean, index: number) => {
			const fieldsUpdate = getValues("fields");

			update(index, {
				...fieldsUpdate[index],
				required: checked,
			});
		},
		[],
	);

	const handleChangeType = useCallback(
		(newType: SurveyFieldType, index: number) => {
			const fieldsUpdate = getValues("fields");
			update(index, {
				...fieldsUpdate[index],
				type: newType,
			});
		},
		[],
	);

	const getValue = useCallback((index: number) => {
		const fieldsList = getValues("fields");
		return fieldsList[index];
	}, []);

	return (
		<Box component="div" className="question-boxes">
			<Box component="div" className="flex flex-col gap-6">
				<DndContext sensors={sensors} onDragEnd={handleDragEnd}>
					<SortableContext
						items={fields.map((item) => item.uuid)}
						strategy={verticalListSortingStrategy}
					>
						{fields.map((field, _index) => (
							<QuestionItem
								key={field.id}
								id={field.uuid}
								index={_index}
								methods={methods}
								error={errors?.fields?.[_index]?.options?.message}
								value={getValue(_index)}
								onChangeQuestionType={handleChangeType}
								onChangeRequired={handleChangeRequired}
								onRemove={remove}
							/>
						))}
					</SortableContext>
				</DndContext>
			</Box>
			<div className="h-6"></div>
			{errors.fields?.message && (
				<Typography component="p" className="text-xs text-red-600 mb-6">
					{errors.fields?.message}
				</Typography>
			)}
			<ButtonAddQuestion onClick={handleAddQuestion} />
		</Box>
	);
};
export default memo(SurveyQuestionBoxes);
